import { useState, useEffect } from "react";
import {
  Input,
  Button,
  Form,
  Typography,
  ConfigProvider,
  Modal,
} from "antd";
import { FaUser, FaLock, FaEnvelope } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Api from "../api/Api";

const { Title, Text } = Typography;

/* ─── Keyframe styles injetados uma vez ─── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');

  * { box-sizing: border-box; }

  .login-root {
    display: flex;
    height: 100vh;
    width: 100%;
    align-items: center;
    justify-content: center;
    font-family: 'Poppins', sans-serif;
    background: linear-gradient(135deg, #f98244 0%, #d94452 100%);
    padding: 20px;
  }

  .login-card {
    display: flex;
    width: 100%;
    max-width: 1100px;
    height: 700px;
    background: #fff;
    border-radius: 24px;
    overflow: hidden;
    box-shadow: 0 40px 100px rgba(0,0,0,0.25);
    animation: cardEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1);
  }

  @keyframes cardEntrance {
    from { opacity: 0; transform: translateY(30px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0)    scale(1);    }
  }

  /* ── LEFT PANEL ── */
  .login-left {
    flex: 1.2;
    position: relative;
    background: #fffafa; /* Tom quase branco derivado do vermelho do logo */
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  /* Efeito de brilho na borda da união com pulso */
  .login-left::before {
    content: '';
    position: absolute;
    width: 400px; height: 400px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(217,68,82,0.04) 0%, transparent 70%);
    top: -100px; left: -100px;
  }

  .login-left::after {
    content: '';
    position: absolute;
    width: 300px; height: 300px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(249,130,68,0.04) 0%, transparent 70%);
    bottom: -50px; right: -50px;
  }

  @keyframes edgePulse {
    0%, 100% { opacity: 0.4; filter: brightness(1); }
    50% { opacity: 0.8; filter: brightness(1.3); }
  }

  @keyframes panelSlideIn {
    from { transform: translateX(-110%); opacity: 0; }
    to   { transform: translateX(0);     opacity: 1; }
  }

  .login-left::before {
    content: '';
    position: absolute;
    width: 520px; height: 520px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(249,130,68,0.14) 0%, transparent 70%);
    top: -110px; left: -110px;
    animation: orb 5s ease-in-out infinite;
    z-index: 1;
  }

  /* Orb inferior movido para não conflitar com o novo ::after */
  .orb-bottom {
    content: '';
    position: absolute;
    width: 420px; height: 420px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(217,68,82,0.12) 0%, transparent 70%);
    bottom: -90px; right: 30px;
    animation: orb 5s ease-in-out infinite reverse;
    z-index: 1;
  }

  @keyframes orb {
    0%, 100% { transform: scale(1);    opacity: 0.7; }
    50%       { transform: scale(1.15); opacity: 1;   }
  }

  .illus-wrap {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    padding: 32px 24px;
    animation: illustIn 1.1s cubic-bezier(0.22, 1, 0.36, 1) 0.25s both;
  }

  @keyframes illustIn {
    from { transform: translateY(48px) scale(0.88); opacity: 0; }
    to   { transform: translateY(0)    scale(1);    opacity: 1; }
  }

  .illus-img {
    width: 480px;
    max-width: 90%;
    object-fit: contain;
    filter: drop-shadow(0 20px 40px rgba(217,68,82,0.15));
    animation: float 5.5s ease-in-out infinite;
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-14px); }
  }

  .illus-text {
    text-align: center;
    animation: fadeUp 1s ease 0.7s both;
  }

  .illus-text h2 {
    font-size: 22px;
    font-weight: 600;
    color: #fff;
    letter-spacing: 0.4px;
    margin: 0 0 6px;
  }

  .illus-text p {
    font-size: 13px;
    color: rgba(255,255,255,0.42);
    font-weight: 300;
    letter-spacing: 0.3px;
    margin: 0;
  }

  .dots {
    display: flex;
    gap: 9px;
    justify-content: center;
    margin-top: 14px;
  }

  .dot {
    width: 8px; height: 8px;
    border-radius: 50%;
    animation: dotPulse 2.2s ease-in-out infinite;
  }

  .dot-1 { background: #f98244; animation-delay: 0s; }
  .dot-2 { background: #d94452; animation-delay: 0.35s; }
  .dot-3 { background: rgba(255,255,255,0.4); animation-delay: 0.7s; }

  @keyframes dotPulse {
    0%, 100% { transform: scale(1);   opacity: 0.6; }
    50%       { transform: scale(1.5); opacity: 1;   }
  }

  /* ── RIGHT PANEL ── */
  .login-right {
    flex: 0.8;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    padding: 60px 40px;
  }

  @keyframes rightFadeIn {
    from { opacity: 0; transform: translateX(20px); }
    to   { opacity: 1; transform: translateX(0);    }
  }

  .login-right::before {
    content: '';
    position: absolute;
    bottom: -90px; right: -90px;
    width: 340px; height: 340px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(249,130,68,0.07), rgba(217,68,82,0.07));
    pointer-events: none;
  }

  .login-right::after {
    content: '';
    position: absolute;
    top: -60px; left: -60px;
    width: 220px; height: 220px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(217,68,82,0.05), transparent);
    pointer-events: none;
  }

.form-box {
  width: 100%;
  max-width: 440px;
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  overflow: visible;
  transform: translateY(-130px); /* Sobe o modal para manter a altura que você gostou */
}

.form-sub {
  font-size: 13px;
  color: #aaa;
  margin-bottom: 28px;  /* ← Aumentei de 10px para 28px */
  font-weight: 300;
  letter-spacing: 0.2px;
  text-align: center;
  animation: slideFromLeft 0.8s ease 0.7s both;
  display: block;
}


  @keyframes logoIn {
    from { opacity: 0; transform: scale(0.78) translateY(-12px); }
    to   { opacity: 1; transform: scale(1)    translateY(0);     }
  }

  .logo-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: -10px; /* Usei um valor negativo para encostar de vez o símbolo no texto */
    margin: 220px auto 4px; /* Mantive a margem que você definiu manualmente */
    animation: logoIn 0.8s ease 0.5s both;
  }

  .logo-symbol {
    height: 114px; /* Aumentei de 48px para 64px */
    width: auto;
    object-fit: contain;
    filter: drop-shadow(0 4px 10px rgba(217,68,82,0.15));
  }

  .logo-text {
    font-size: 32px; /* Aumentei de 36px para 52px */
    font-weight: 700;
    color: #1a1a1a;
    letter-spacing: 2px;
    font-family: 'Poppins', sans-serif;
  }

  .form-heading {
    font-size: 32px !important;
    font-weight: 700 !important;
    color: #1a1a1a !important;
    margin-bottom: 4px !important;
    text-align: center;
  }

  .form-sub {
    font-size: 13px;
    color: #ccc;
    margin-bottom: 32px;
    font-weight: 400;
    letter-spacing: 0.2px;
    text-align: center;
    display: block;
  }

  @keyframes slideFromLeft {
    from { opacity: 0; transform: translateX(-18px); }
    to   { opacity: 1; transform: translateX(0);     }
  }

  .field-anim-1 { animation: fieldIn 0.8s ease 0.75s both; }
  .field-anim-2 { animation: fieldIn 0.8s ease 0.85s both; }

  @keyframes fieldIn {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0);    }
  }

  .forgot-wrap {
    text-align: right;
    margin-top: -8px;
    margin-bottom: 24px;
    animation: fieldIn 0.8s ease 0.92s both;
  }

  .forgot-link {
    font-size: 12px;
    color: #d94452;
    cursor: pointer;
    font-weight: 500;
    opacity: 0.8;
    transition: opacity 0.2s;
    font-family: 'Poppins', sans-serif;
  }

  .forgot-link:hover { opacity: 1; }

  .btn-submit {
    height: 54px !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    letter-spacing: 1.5px !important;
    text-transform: uppercase;
    border-radius: 14px !important;
    border: none !important;
    background: linear-gradient(135deg, #f98244, #d94452) !important;
    box-shadow: 0 8px 24px rgba(217,68,82,0.3) !important;
    transition: all 0.3s ease !important;
    position: relative;
    overflow: hidden;
    animation: btnIn 0.8s ease 1s both;
  }

  .btn-submit::before {
    content: '';
    position: absolute;
    top: 0; left: -100%;
    width: 100%; height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
    transition: left 0.5s;
  }

  .btn-submit:hover::before { left: 100%; }
  .btn-submit:hover { transform: translateY(-2px) !important; box-shadow: 0 14px 32px rgba(217,68,82,0.42) !important; }
  .btn-submit:active { transform: translateY(0) !important; }

  @keyframes btnIn {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0);    }
  }

  .footer-brand {
    text-align: center;
    margin-top: 32px;
    font-size: 12px;
    color: #999;
    letter-spacing: 0.5px;
    animation: fadeUp 0.8s ease 1.1s both;
  }

  .footer-brand strong { color: #d94452; font-weight: 600; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0);    }
  }

  .footer-left {
    position: absolute;
    bottom: 40px;
    text-align: center;
    font-size: 13px;
    color: #666;
    font-weight: 500;
    letter-spacing: 0.5px;
    animation: fadeUp 1s ease 0.9s both;
  }

  .footer-left strong { color: #d94452; font-weight: 700; }

  /* Responsive */
  @media (max-width: 992px) {
    .login-card { height: auto; max-width: 500px; flex-direction: column; }
    .login-left { display: none; }
    .login-right { padding: 40px 20px; }
  }
`;

const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState("");
  const [senhaAtual, setSenhaAtual] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "OTUMA - Login";

    // Inject styles once
    if (!document.getElementById("otuma-login-styles")) {
      const tag = document.createElement("style");
      tag.id = "otuma-login-styles";
      tag.textContent = STYLES;
      document.head.appendChild(tag);
    }
  }, []);

  const onFinish = async (values) => {
    try {
      setLoading(true);
      localStorage.removeItem("token");
      localStorage.removeItem("loginTime");

      const result = await Api.Login({
        email: values.email,
        password: values.password,
      });

      if (result.success) {
        toast.success("Login efetuado com sucesso");
        localStorage.setItem("token", JSON.stringify(result.user));
        localStorage.setItem("loginTime", Date.now());
        setTimeout(() => navigate("/dashboard"), 2000);
      } else if (result.message === "Ativar") {
        setEmail(values.email);
        setSenhaAtual(values.password);
        localStorage.setItem("NPW", JSON.stringify(result.senha));
        toast.warning("Para continuar, altere sua senha");
        setShowModal(true);
      } else {
        toast.error(result.message || "Credenciais incorretas");
      }
    } catch (error) {
      toast.error(`Erro: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAlterarSenha = async (values) => {
    try {
      if (values.nova !== values.confirmar) {
        toast.error("As senhas não coincidem");
        return;
      }
      const usuario = JSON.parse(localStorage.getItem("NPW"));
      const result = await Api.AlterPW({ id: usuario.id, nova_senha: values.nova });

      if (result.success) {
        toast.success("Senha alterada com sucesso!");
        setShowModal(false);
        navigate("/");
      } else {
        toast.error(result.message || "Erro ao alterar senha");
      }
    } catch (error) {
      toast.error(`Erro: ${error.message}`);
    }
  };

  const inputStyle = {
    padding: "13px 16px 13px 44px",
    borderRadius: 12,
    fontSize: 14,
    fontFamily: "'Poppins', sans-serif",
    height: 54,
    background: "#f8f9fa",
    border: "1px solid #eee",
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#d94452",
          borderRadius: 14,
          fontFamily: "'Poppins', sans-serif",
        },
        components: {
          Input: {
            hoverBorderColor: "#d94452",
            activeBorderColor: "#d94452",
            activeShadow: "0 0 0 4px rgba(217,68,82,0.1)",
          },
        },
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} toastStyle={{ borderRadius: 12, fontFamily: "'Poppins', sans-serif" }} />

      <div className="login-root">
        <div className="login-card">
          {/* ── LEFT: Illustration ── */}
          <div className="login-left">
            <div className="illus-wrap">
              <img
                className="illus-img"
                src="/login.png"
                alt="Login Illustration"
                style={{ width: '480px', maxWidth: '90%' }}
              />
              <div className="illus-text">
                <h2 style={{ color: '#333', fontSize: '24px', fontWeight: '600' }}>Bem-vindo de volta</h2>
                <p style={{ color: '#aaa', fontSize: '14px' }}>Faça login para acessar o painel</p>
                <div className="dots">
                  <div className="dot dot-1" />
                  <div className="dot dot-2" />
                  <div className="dot dot-3" />
                </div>
              </div>
            </div>
            <div className="footer-left">
              &copy; {new Date().getFullYear()} <strong>Mil Vendas</strong>. Todos os direitos reservados.
            </div>
          </div>

          {/* ── RIGHT: Form ── */}
          <div className="login-right">
            <div className="form-box">
              <div className="logo-container">
                <img src="/OTUMASIMBOLO.png" alt="Símbolo OTUMA" className="logo-symbol" />
                <span className="logo-text">OTUMA</span>
              </div>
              <Title level={3} className="form-heading">Acesso ao sistema</Title>
              <Text className="form-sub">Insira suas credenciais para continuar</Text>

              <Form layout="vertical" onFinish={onFinish}>
                <div className="field-anim-1">
                  <Form.Item
                    name="email"
                    rules={[{ required: true, message: "Por favor, insira seu email!" }]}
                  >
                    <Input
                      size="large"
                      prefix={<FaEnvelope style={{ color: "#ccc", marginRight: 8 }} />}
                      placeholder="Email"
                      style={inputStyle}
                    />
                  </Form.Item>
                </div>

                <div className="field-anim-2">
                  <Form.Item
                    name="password"
                    rules={[{ required: true, message: "Por favor, insira sua senha!" }]}
                  >
                    <Input.Password
                      size="large"
                      prefix={<FaLock style={{ color: "#ccc", marginRight: 8 }} />}
                      placeholder="Senha"
                      style={inputStyle}
                    />
                  </Form.Item>
                </div>

                <div className="forgot-wrap">
                  <span className="forgot-link">Esqueceu sua senha?</span>
                </div>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    loading={loading}
                    className="btn-submit"
                  >
                    Entrar
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </div>
        </div>
      </div>

      {/* ── MODAL: Alterar Senha ── */}
      <Modal
        open={showModal}
        title={<Title level={4} style={{ margin: 0, color: "#1a1a1a", fontFamily: "'Poppins', sans-serif" }}>Alterar senha</Title>}
        onCancel={() => setShowModal(false)}
        footer={null}
        centered
        styles={{ body: { padding: "24px 24px 8px" } }}
        width={420}
      >
        <Form layout="vertical" onFinish={handleAlterarSenha}>
          <Form.Item label="Senha atual">
            <Input.Password value={senhaAtual} disabled size="large" style={{ borderRadius: 14 }} />
          </Form.Item>
          <Form.Item
            label="Nova senha"
            name="nova"
            rules={[{ required: true, message: "Informe a nova senha" }]}
          >
            <Input.Password placeholder="Nova senha" size="large" style={{ borderRadius: 14 }} />
          </Form.Item>

          <Form.Item
            label="Confirmar nova senha"
            name="confirmar"
            rules={[{ required: true, message: "Confirme a nova senha" }]}
          >
            <Input.Password placeholder="Confirme a nova senha" size="large" style={{ borderRadius: 14 }} />
          </Form.Item>

          <Form.Item style={{ marginTop: 28 }}>
            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              style={{
                height: 50,
                fontWeight: 600,
                letterSpacing: 1,
                borderRadius: 14,
                border: "none",
                background: "linear-gradient(135deg, #f98244, #d94452)",
                boxShadow: "0 8px 24px rgba(217,68,82,0.3)",
              }}
            >
              CONFIRMAR ALTERAÇÃO
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </ConfigProvider>
  );
};

export default LoginPage;